import { useCallback, type ReactNode } from "react";
import {
  getRetailerCategories,
  getRetailerOverview,
  getRetailerProducts,
  getRetailerTraffic,
  type RetailerQuery,
} from "@/api/client";
import type { RetailerCategoryRow, RetailerProductRow } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { EngagementCharts } from "@/components/EngagementCharts";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { StatCard } from "@/components/StatCard";
import { TrafficHeatmap } from "@/components/TrafficHeatmap";
import {
  ChartSkeleton,
  ErrorState,
  TableSkeleton,
} from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData, type ApiDataState } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useUrlListFilter } from "@/hooks/useUrlFilters";
import {
  formatCompact,
  formatCurrency,
  formatCurrencyCompact,
  formatNumber,
} from "@/lib/format";

const productColumns: Column<RetailerProductRow>[] = [
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Product name",
    cell: (r) => r.productName ?? "—",
    sortValue: (r) => r.productName,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

const categorySalesColumns: Column<RetailerCategoryRow>[] = [
  { header: "Category", cell: (r) => r.category },
  { header: "Sales", align: "right", cell: (r) => formatCurrency(r.sales) },
];

const categoryUnitsColumns: Column<RetailerCategoryRow>[] = [
  { header: "Category", cell: (r) => r.category },
  { header: "Units", align: "right", cell: (r) => formatNumber(r.units) },
];

/** Five-row ranked slice of the category list. */
function rankedSlice(
  categories: RetailerCategoryRow[],
  measure: "sales" | "units",
  direction: "top" | "bottom",
): RetailerCategoryRow[] {
  return [...categories]
    .sort((a, b) =>
      direction === "top" ? b[measure] - a[measure] : a[measure] - b[measure],
    )
    .slice(0, 5);
}

/** Card whose body tracks an ApiDataState: skeleton → error+retry → content. */
function AsyncCard<T>({
  title,
  description,
  state,
  skeleton,
  action,
  children,
}: {
  title: string;
  description?: string;
  state: ApiDataState<T> & { reload: () => void };
  skeleton: ReactNode;
  /** Title-row action (e.g. a CSV button), rendered once data is ready. */
  action?: (data: T) => ReactNode;
  children: (data: T) => ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        {action && state.status === "ready" ? action(state.data) : null}
      </CardHeader>
      <CardContent>
        {state.status === "loading" ? skeleton : null}
        {state.status === "error" ? (
          <ErrorState message={state.message} onRetry={state.reload} />
        ) : null}
        {state.status === "ready" ? children(state.data) : null}
      </CardContent>
    </Card>
  );
}

export function RetailerActivityPage() {
  const filters = useProductFilterOptions();
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");
  // Key "subcategory": the options are level-2 categories sent as
  // subcategoryId, and sibling pages use "category" for level-1 ids.
  const [categoryIds, setCategoryIds, category] =
    useUrlListFilter("subcategory");
  // Each card fetches independently so the requests run in parallel and a
  // slow widget doesn't hold the rest of the page hostage.
  const useRetailerData = <T,>(
    get: (token: string, query: RetailerQuery) => Promise<T>,
  ) =>
    useApiData(
      useCallback(
        (token: string) => get(token, { storeId: store, subcategoryId: category }),
        [get, store, category],
      ),
    );
  const overview = useRetailerData(getRetailerOverview);
  const categories = useRetailerData(getRetailerCategories);
  const traffic = useRetailerData(getRetailerTraffic);
  const products = useRetailerData(getRetailerProducts);

  const tiles = overview.status === "ready" ? overview.data.tiles : null;
  const engagement =
    overview.status === "ready" ? overview.data.engagement : null;

  return (
    <div
      className="flex flex-col gap-6"
      aria-busy={overview.status === "loading"}
    >
      <div>
        <h1 className="text-xl font-semibold">Store activity</h1>
        <p className="text-sm text-muted-foreground">
          Transactions, categories, products, and store traffic, last 90 days.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <MultiFilterSelect
          label="Store"
          allLabel="All stores"
          values={storeIds}
          onChange={setStoreIds}
          disabled={filters.disabled}
          options={filters.storeOptions}
        />
        <MultiFilterSelect
          label="Category"
          allLabel="All categories"
          values={categoryIds}
          onChange={setCategoryIds}
          disabled={filters.disabled}
          options={filters.categoryOptions}
        />
      </div>

      {overview.status === "error" ? (
        <ErrorState message={overview.message} onRetry={overview.reload} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Total transactions"
            value={tiles && formatCompact(tiles.totalTransactions)}
          />
          <StatCard
            label="Loyalty transactions"
            value={tiles && formatCompact(tiles.loyaltyTransactions)}
          />
          <StatCard
            label="Total units sold"
            value={tiles && formatCompact(tiles.totalUnits)}
          />
          <StatCard
            label="Estimated total shoppers"
            value={tiles && formatCompact(tiles.estimatedShoppers)}
          />
          <StatCard
            label="Loyalty shoppers"
            value={tiles && formatCompact(tiles.loyaltyShoppers)}
          />
          <StatCard
            label="Total sales"
            value={tiles && formatCurrencyCompact(tiles.totalSales)}
          />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {(
          [
            ["Top 5 categories by sales", "sales", "top"],
            ["Bottom 5 categories by sales", "sales", "bottom"],
            ["Top 5 categories by units", "units", "top"],
            ["Bottom 5 categories by units", "units", "bottom"],
          ] as const
        ).map(([title, measure, direction]) => (
          <AsyncCard
            key={title}
            title={title}
            state={categories}
            skeleton={<TableSkeleton rows={5} />}
          >
            {(data) => (
              <DataTable
                columns={
                  measure === "sales"
                    ? categorySalesColumns
                    : categoryUnitsColumns
                }
                rows={rankedSlice(data.categories, measure, direction)}
                rowKey={(r) => r.category}
                emptyMessage="No category activity"
              />
            )}
          </AsyncCard>
        ))}
      </div>

      <AsyncCard
        title="Store traffic"
        description="Units sold by day of week and hour of day (Eastern time)"
        state={traffic}
        skeleton={<ChartSkeleton height={420} />}
      >
        {(data) => <TrafficHeatmap cells={data.cells} />}
      </AsyncCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <AsyncCard
          title="Top 1,000 products by units"
          state={products}
          skeleton={<TableSkeleton />}
          action={(data) => (
            <CsvDownloadButton
              name="top-products-by-units"
              columns={productColumns}
              rows={data.top}
            />
          )}
        >
          {(data) => (
            <DataTable
              columns={productColumns}
              rows={data.top}
              rowKey={(r) => r.upc}
              maxHeight="28rem"
              emptyMessage="No product activity"
            />
          )}
        </AsyncCard>
        <AsyncCard
          title="Bottom 1,000 products by units"
          state={products}
          skeleton={<TableSkeleton />}
          action={(data) => (
            <CsvDownloadButton
              name="bottom-products-by-units"
              columns={productColumns}
              rows={data.bottom}
            />
          )}
        >
          {(data) => (
            <DataTable
              columns={productColumns}
              rows={data.bottom}
              rowKey={(r) => r.upc}
              maxHeight="28rem"
              emptyMessage="No product activity"
            />
          )}
        </AsyncCard>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Shopper engagement</CardTitle>
          <CardDescription>
            Shoppers bucketed by transactions in these ranges: heavy 13+,
            medium 2–12, light 1
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overview.status === "loading" ? <ChartSkeleton height={180} /> : null}
          {engagement ? <EngagementCharts engagement={engagement} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
